import {Box, getValueByDevice, Stack, useDevice} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, isNotNil} from 'ramda';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  SALES_LEAGUE_PODIUM_COLUMN_ORDER,
  SALES_LEAGUE_PODIUM_MEDALS,
  SALES_LEAGUE_PODIUM_ROW_ORDER,
  SALES_LEAGUE_PODIUM_RUNNER_UP_FLEX,
  SALES_LEAGUE_PODIUM_WINNER_FLEX,
  SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX,
} from '../constants/SALES_LEAGUE_PODIUM';
import {SalesLeaguePodiumCard, SalesLeaguePodiumEntry} from './SalesLeaguePodiumCard';

const PODIUM_SPACING = 4;

/** The winner card is the wider one, and only while the cards sit in a row. */
const getSlotFlex = (isPodiumRow: boolean, placeIndex: number) =>
  match({isPodiumRow, isWinner: placeIndex === SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX})
    .returnType<number | undefined>()
    .with({isPodiumRow: false}, always(undefined))
    .with({isWinner: true}, always(SALES_LEAGUE_PODIUM_WINNER_FLEX))
    .otherwise(always(SALES_LEAGUE_PODIUM_RUNNER_UP_FLEX));

interface SalesLeaguePodiumProps extends RequiredTestIdProps {
  /** One slot per place, ordered by rank. A nullish slot renders as unfilled. */
  entries: (SalesLeaguePodiumEntry | Nullish)[];
  periodLabel: string;
  unfilledSubtitle: string;
  unfilledStatLabels: string[];
}

/**
 * Silver, gold, bronze while there is room for the podium metaphor; below that the cards
 * stack winner-first, because a stacked 2-1-3 reads as a board that lost its ordering.
 */
export function SalesLeaguePodium(props: SalesLeaguePodiumProps) {
  const device = useDevice();
  const isPodiumRow = getValueByDevice(device, false, false, true);

  const slots = SALES_LEAGUE_PODIUM_MEDALS.map((medal, placeIndex) => ({
    medal,
    placeIndex,
    entry: props.entries[placeIndex],
  }));

  const order = isPodiumRow ? SALES_LEAGUE_PODIUM_ROW_ORDER : SALES_LEAGUE_PODIUM_COLUMN_ORDER;
  const orderedSlots = order.map((placeIndex) => slots[placeIndex]).filter(isNotNil);

  return (
    <Stack
      direction={isPodiumRow ? 'row' : 'column'}
      align={isPodiumRow ? 'center' : 'stretch'}
      spacing={PODIUM_SPACING}
      data-testid={suffixTestId('podium', props)}
    >
      {orderedSlots.map((slot) => (
        <Box key={slot.placeIndex} flex={getSlotFlex(isPodiumRow, slot.placeIndex)}>
          <SalesLeaguePodiumCard
            placeIndex={slot.placeIndex}
            medal={slot.medal}
            entry={slot.entry}
            periodLabel={props.periodLabel}
            unfilledSubtitle={props.unfilledSubtitle}
            unfilledStatLabels={props.unfilledStatLabels}
            data-testid={suffixTestId(`place-${slot.placeIndex}`, props)}
          />
        </Box>
      ))}
    </Stack>
  );
}
